import { useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Plus, Trash2, Upload, Loader2 } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { btnGhost } from "../../components/ui";

const input = "w-full rounded-lg border border-gray-200 px-2.5 py-1.5 text-sm focus:border-bo-green focus:outline-none";
const newId = () => Math.random().toString(36).slice(2, 10);

export function ImageField({ value, onChange, testid }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file); setBusy(true);
    try { onChange((await api.post("/admin/site/images", fd)).data.url); toast.success("Imagem carregada"); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-2">
      {value && <img src={value} alt="" className="h-10 w-14 rounded object-cover" />}
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder="URL (https://… ou /api/bo/images/…)" className={input} data-testid={testid} />
      <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={(e) => upload(e.target.files[0])} data-testid={`${testid}-file`} />
      <button type="button" onClick={() => ref.current.click()} className={`${btnGhost} shrink-0 px-2 py-1.5`} title="Carregar imagem" data-testid={`${testid}-upload`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}</button>
    </div>
  );
}

export function FieldInput({ f, value, onChange, testid, icons }) {
  if (f.type === "readonly") return <p className="py-1.5 text-sm font-semibold" data-testid={testid}>{f.labels?.[value] || value}</p>;
  if (f.type === "bool") return <label className="flex items-center gap-1.5 text-xs"><input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} data-testid={testid} />{f.label}</label>;
  if (f.type === "image") return <ImageField value={value || ""} onChange={onChange} testid={testid} />;
  if (f.type === "select") return <select value={value} onChange={(e) => onChange(e.target.value)} className={input} data-testid={testid}>{(f.options || icons).map((o) => <option key={o} value={o}>{o}</option>)}</select>;
  if (f.type === "date") return <input type="date" value={value || ""} onChange={(e) => onChange(e.target.value)} className={input} data-testid={testid} />;
  if (f.type === "textarea") return <textarea rows={2} value={value || ""} onChange={(e) => onChange(e.target.value)} className={input} data-testid={testid} />;
  return <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={f.placeholder} className={input} data-testid={testid} />;
}

export function ListEditor({ items, onChange, fields, blank, name, fixed = false, icons }) {
  const set = (i, k, v) => onChange(items.map((it, j) => (j === i ? { ...it, [k]: v } : it)));
  const move = (i, d) => { const a = [...items]; [a[i], a[i + d]] = [a[i + d], a[i]]; onChange(a); };
  return (
    <div className="flex flex-col gap-2" data-testid={`list-${name}`}>
      {items.map((it, i) => (
        <div key={it.id || it.key || i} className="grid gap-2 rounded-xl border border-gray-100 bg-gray-50/50 p-3 sm:grid-cols-2 lg:grid-cols-4" data-testid={`list-${name}-item-${i}`}>
          {fields.map((f) => (
            <div key={f.key} className={f.wide ? "sm:col-span-2" : ""}>
              {f.type !== "bool" && <p className="mb-0.5 text-[11px] font-semibold text-gray-500">{f.label}</p>}
              <FieldInput f={f} value={it[f.key]} onChange={(v) => set(i, f.key, v)} testid={`${name}-${i}-${f.key}`} icons={icons} />
            </div>
          ))}
          <div className="flex items-end gap-1">
            <button type="button" disabled={i === 0} onClick={() => move(i, -1)} className={`${btnGhost} px-2 py-1`} aria-label="Subir" data-testid={`${name}-${i}-up`}><ArrowUp className="h-3.5 w-3.5" /></button>
            <button type="button" disabled={i === items.length - 1} onClick={() => move(i, 1)} className={`${btnGhost} px-2 py-1`} aria-label="Descer" data-testid={`${name}-${i}-down`}><ArrowDown className="h-3.5 w-3.5" /></button>
            {!fixed && <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))} className={`${btnGhost} px-2 py-1 text-red-600`} aria-label="Remover" data-testid={`${name}-${i}-remove`}><Trash2 className="h-3.5 w-3.5" /></button>}
          </div>
        </div>
      ))}
      {!fixed && <button type="button" onClick={() => onChange([...items, { ...blank, id: newId() }])} className={`${btnGhost} self-start`} data-testid={`${name}-add`}><Plus className="h-4 w-4" />Adicionar</button>}
    </div>
  );
}

export const OBJ = (fields, value, onChange, prefix) => (
  <div className="grid gap-3 sm:grid-cols-2">{fields.map((f) => (
    <div key={f.key} className={f.wide ? "sm:col-span-2" : ""}>
      {f.type !== "bool" && <p className="mb-0.5 text-[11px] font-semibold text-gray-500">{f.label}</p>}
      <FieldInput f={f} value={value[f.key]} onChange={(v) => onChange({ ...value, [f.key]: v })} testid={`${prefix}-${f.key}`} />
    </div>))}</div>
);
